import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getRefineryById, verifyOrRejectRefinery } from "../../../redux/api";
import BackButton from "../../../components/BackButton";

const RefineryConsignmentDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchDetails = async () => {
    try {
      const res = await getRefineryById(id);
      if (res.status === 200 && res.data.data.length > 0) {
        setData(res.data.data[0]);
      } else {
        toast.error("No consignment found");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [id]);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      await verifyOrRejectRefinery(data.shipmentId, "APPROVED", {
        status: "true"
      });
      toast.success("Consignment verified.");
      setShowVerifyModal(false);
      fetchDetails();
    } catch (err) {
      toast.error("Verification failed.");
    } finally {
      setVerifying(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return toast.error("Reject reason required");
    try {
      await verifyOrRejectRefinery(data.shipmentId, "REJECTED", {
        status: "false",
        RefinedDocumentRejectedReasons: rejectReason
      });
      toast.success("Consignment rejected.");
      setShowRejectModal(false);
      setRejectReason("");
      fetchDetails();
    } catch (err) {
      toast.error("Rejection failed.");
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6 text-red-500">No data found</div>;

  const dore = data.doreBarDetails || [];
  const docs = data.documents?.[0] || {};
  const status = data.refinedDocumentStatus;

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <BackButton /> Refinery Consignment Details
      </h2>

      <div className="flex justify-between items-center mb-4">
        <div>
          <span className="text-lg font-semibold">Status:</span>{" "}
          <span
            className={`font-bold ${
              status === "APPROVED"
                ? "text-green-600"
                : status === "REJECTED"
                ? "text-red-600"
                : "text-yellow-600"
            }`}
          >
            {status === "APPROVED"
              ? "Approved"
              : status === "REJECTED"
              ? "Rejected"
              : "Pending"}
          </span>
        </div>

        {status === "PENDING" && (
          <div className="flex gap-4">
            <button
              className="bg-green-600 text-white px-4 py-2 rounded-lg"
              onClick={() => setShowVerifyModal(true)}
            >
              Verify
            </button>
            <button
              className="bg-red-600 text-white px-4 py-2 rounded-lg"
              onClick={() => setShowRejectModal(true)}
            >
              Reject
            </button>
          </div>
        )}
      </div>

      <section className="bg-white p-4 shadow rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Dore Bar Details</h3>
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2 border">Serial No</th>
              <th className="p-2 border">Weight (kg)</th>
              <th className="p-2 border">Purity (%)</th>
              <th className="p-2 border">Gold Content (g)</th>
              <th className="p-2 border">Other Metals</th>
            </tr>
          </thead>
          <tbody>
            {dore.map((bar, idx) => (
              <tr key={idx}>
                <td className="p-2 border">{bar.serialNumber}</td>
                <td className="p-2 border">{bar.weightKg}</td>
                <td className="p-2 border">{bar.purityPercent}</td>
                <td className="p-2 border">{bar.goldContentG}</td>
                <td className="p-2 border">
                  {bar.otherMetals
                    ? Object.entries(JSON.parse(bar.otherMetals))
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ")
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-white p-4 shadow rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Documents</h3>
        <ul className="space-y-3">
          {Object.entries(docs).map(([key, value]) => {
            if (!Array.isArray(value)) return null;
            return value.map((url, idx) => (
              <li key={`${key}-${idx}`}>
                <span className="font-medium capitalize">
                  {key.replace(/([A-Z])/g, " $1")}:
                </span>{" "}
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  View Document
                </a>
              </li>
            ));
          })}
          {data.refinedDocument?.length > 0 && (
            <ul className="space-y-3">
              {data.refinedDocument.map((url, index) => (
                <li key={`refined-doc-${index}`}>
                  <span className="font-medium">Refined Documents :</span>{" "}
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline"
                  >
                    View Document
                  </a>
                </li>
              ))}
            </ul>
          )}
        </ul>
      </section>

      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[400px]">
            <h2 className="text-xl font-semibold mb-4">Verify Consignment</h2>
            <p className="mb-4">
              Are you sure you want to verify this consignment?
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="border px-4 py-2 rounded"
                onClick={() => setShowVerifyModal(false)}
              >
                Cancel
              </button>
              <button
                onClick={handleVerify}
                className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
                disabled={verifying}
              >
                {verifying ? "Verifying..." : "Confirm Verify"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[400px]">
            <h2 className="text-xl font-semibold mb-4">Reject Consignment</h2>
            <textarea
              className="w-full border border-gray-300 rounded p-2"
              rows="4"
              placeholder="Enter reason for rejection"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex justify-end gap-4 mt-4">
              <button
                className="border px-4 py-2 rounded"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="bg-red-600 text-white px-4 py-2 rounded disabled:opacity-50"
                disabled={!rejectReason.trim()}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefineryConsignmentDetails;
